import {
  Fragment,
  type ReactNode,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import type { ListedWorkspace } from "../src/workspace-status.ts";
import {
  CheckIcon,
  ChevronDownIcon,
  LinkIcon,
  LogOutIcon,
  MoonIcon,
  NavGlyph,
  PlusIcon,
  ShortcutsIcon,
  WorkspaceIcon,
} from "./icons.tsx";
import { OptionMenu } from "./option-menu.tsx";
import { PendingCount } from "./pending-count.tsx";
import { initials } from "./phone-menu.tsx";
import {
  INBOX_SECTION,
  mailGroups,
  type NavRow,
  type ScopeNav,
  type ShellNav,
  scopeOptions,
} from "./shell-nav.ts";
import { Tip } from "./tip.tsx";

/*
 * The shell's navigation in two levels (LEA-501; docs/2026-11-03-lea-501-two-level-shell-plan.md).
 *
 * The app rail is 64 px of icons, always there and never collapsed: the LeadCue mark, the client's
 * monogram (the workspace switcher, `W`), the sections this person may reach, and at the foot
 * Settings and the account. The workspace sits at its head because it is the scope of everything on
 * screen. The Inbox's own rows (the work, the labels, the boxes, saved views and More) live in the
 * section panel beside it, drawn only in the inbox, so Campaigns, Mailboxes and Standing answers
 * show no inbox rows and Settings draws its groups in the same place.
 *
 * Until 2026-11-03 one 248 px rail held all four kinds of thing; at 1440 x 900 its pages sat under
 * the fold and the current page's mark scrolled out of view (critique of that day, 23/40). Its
 * collapse by `[` and a click on empty space went with it: the panel has no collapse by choice and
 * folds on its own into the list's tabs where the window is too narrow (`src/pane-layout.ts`). An
 * overlay sidebar was rejected on 2026-09-30 ("the overlay only sidebar is wrong here"), so a folded
 * panel never opens over the list.
 *
 * What the rows are is decided in `shell-nav.ts`, from what this person may reach; this only draws
 * them.
 */

/** A workspace in the switcher, with this person's conversations needing a reply where it has the inbox. */
export type RailWorkspace = ListedWorkspace & { readonly needsReply?: number | undefined };

export type RailProps = {
  workspaces: readonly RailWorkspace[];
  /** Null while the workspace list is loading, failed, or resolves to nothing the rep can see. */
  current: RailWorkspace | null;
  /** What the scope slot says when there is no workspace to name. */
  workspaceStatus: string;
  /** The rows for the workspace on screen; null while there is none. */
  nav: ShellNav | null;
  /**
   * Whether the inbox's panel is drawn beside the rail: where it is, the current inbox row carries
   * the gold stroke and the Inbox icon gives it up; folded, the icon keeps it.
   */
  panelShown: boolean;
  /** Follows a link in place; the caller decides whether unsaved work allows it. */
  onNavigate: (href: string) => void;
  onWorkspace: (id: string) => void;
  /** Whether the workspace switcher is open, which `W` can do from anywhere. */
  switcherOpen: boolean;
  onSwitcherOpen: (open: boolean) => void;
  /** Where "Add workspace" goes, for a person who may add one; null for anyone else. */
  addWorkspaceHref: string | null;
  user: { name: string; email: string };
  dark: boolean;
  onToggleTheme: () => void;
  onSignOut: () => void;
  /** Opens the keyboard-shortcuts panel. Absent where the page has no shortcuts to list. */
  onShortcuts?: () => void;
  /** Drawn beside the rail's foot: the shortcuts panel, when it is open. */
  footPanel?: ReactNode;
  /** Account-menu rows for what the reader sees in this workspace, after Dark mode. */
  displayOptions?: ReactNode;
};

/** The inbox's panel: its rows, the client named at its head, and the saved views' own controls. */
export type InboxPanelProps = {
  nav: ShellNav;
  /** The fitted width (`src/pane-layout.ts`), which the panel draws itself at. */
  width: number;
  workspaceName: string;
  onNavigate: (href: string) => void;
  /**
   * A saved view's row with its own controls (Rename, Delete), drawn by the inbox
   * (`inbox-v2/saved-views.tsx`); without it a view's row is a plain row.
   */
  viewRow?: (row: NavRow, link: ReactNode) => ReactNode;
  /** Where this browser remembers whether this person keeps the More group open. */
  moreMemory?: string;
};

// The app rail is icons only, so every control there carries a tooltip (`tip.tsx`).

const counted = (count: number): string => count.toLocaleString("en-GB");

/**
 * Whether a label is cut short where it is drawn: measured as the rail's width or the words change,
 * so the labelled rail can say it whole in a tip (Meeting requests on a rail under about 200 px).
 */
function useClipped(text: React.RefObject<HTMLElement | null>): boolean {
  const [clipped, setClipped] = useState(false);
  // After every render, as the words may have changed; an unchanged answer renders nothing.
  useLayoutEffect(() => {
    const element = text.current;
    if (element) setClipped(element.scrollWidth > element.clientWidth + 1);
  });
  useEffect(() => {
    const element = text.current;
    if (!element || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(() =>
      setClipped(element.scrollWidth > element.clientWidth + 1),
    );
    observer.observe(element);
    return () => observer.disconnect();
  }, [text]);
  return clipped;
}

/** The app rail's room for a total under its icon: four characters. */
const capped = (count: number): string => (count > 999 ? "999+" : counted(count));

/**
 * One navigation row. In the app rail the label is hidden from the eye but not from the accessible
 * name, so the row keeps announcing what it is, and the tip says it on hover and focus. A count is
 * read with its noun, so "Needs reply 62" is not left for a screen reader to interpret. A real 0 is
 * shown and dims its row (decision 2 of docs/2026-10-27-inbox-rail-scope-and-counts-plan.md). The
 * current row scrolls into view, so the panel never hides where you are below its fold.
 */
function Row({
  row,
  onNavigate,
  noun = "conversations",
  icons = false,
}: {
  row: NavRow;
  onNavigate: (href: string) => void;
  /** What the count counts, said after it to a screen reader. */
  noun?: string;
  /** The app rail: the count sits under the icon, capped to its room. */
  icons?: boolean;
}) {
  const count = row.count === null ? null : icons ? capped(row.count) : counted(row.count);
  const label = useRef<HTMLSpanElement>(null);
  const link = useRef<HTMLAnchorElement>(null);
  const clipped = useClipped(label);
  useEffect(() => {
    if (row.current) link.current?.scrollIntoView?.({ block: "nearest" });
  }, [row.current]);
  return (
    <span
      className="lcr-rail-item"
      data-sub={row.sub || undefined}
      data-clipped={clipped || undefined}
    >
      <a
        ref={link}
        className="lcr-rail-row"
        href={row.href}
        aria-current={row.current ? "page" : undefined}
        data-marker={row.marked ? undefined : "none"}
        data-empty={row.count === 0 || undefined}
        onClick={(event) => {
          if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
          event.preventDefault();
          onNavigate(row.href);
        }}
      >
        {/* A label view keys itself in a 16 px column, its word on the other rows' label line. */}
        <NavGlyph icon={row.icon} size={row.sub ? 16 : 18} />
        {/* The inner span clips a long label; the outer one carries the stroke, which sits below it. */}
        <span className="lcr-rail-label">
          <span className="lcr-rail-label-text" ref={label}>
            {row.label}
          </span>
        </span>
        {count && (
          <span className="lcr-rail-count">
            <span aria-hidden="true">{count}</span>
            <span className="lcr-visually-hidden">
              , {count} {noun}
            </span>
          </span>
        )}
        {/* Two digits' room, held while the counts are on their way, so nothing shifts when they land. */}
        {!count && row.counting && <PendingCount className="lcr-rail-count" />}
        {/* Counts that failed are unknown, never zero: a dash in the count's place, named. */}
        {!count && !row.counting && row.unknown && (
          <span className="lcr-rail-count" data-unknown="">
            <span aria-hidden="true">–</span>
            <span className="lcr-visually-hidden">, count unavailable</span>
          </span>
        )}
      </a>
      <Tip>
        {row.label}
        {/* In the app rail the tip is the only word beside the number, so it says what it counts. */}
        {count && <span className="lcr-rail-tip-count">{icons ? `${count} ${noun}` : count}</span>}
      </Tip>
    </span>
  );
}

/** Whether the More group is open, remembered per person in this browser; closed by default. */
function useRememberedOpen(key: string | undefined): [boolean, (open: boolean) => void] {
  const [open, setOpen] = useState(() => {
    try {
      return key !== undefined && localStorage.getItem(key) === "open";
    } catch {
      return false;
    }
  });
  return [
    open,
    (next) => {
      setOpen(next);
      try {
        if (key !== undefined) localStorage.setItem(key, next ? "open" : "closed");
      } catch {
        // A browser that keeps nothing still opens and closes the group for this page.
      }
    },
  ];
}

/*
 * The More group (docs/2026-10-19-inbox-sub-inboxes-plan.md, "Where they sit in the UI"): mail
 * nobody must answer, out of the way, last in the panel. No count, badge or bold on it or its rows:
 * it never reaches zero, and a number there would read as work. Closed by default and remembered;
 * shown open while one of its folders is on screen, so the current row is never hidden. Opened, its
 * folders scroll into view: under a short window they used to open below the fold, unseen.
 */
function MoreGroup(props: {
  rows: readonly NavRow[];
  memory: string | undefined;
  onNavigate: (href: string) => void;
}) {
  const [remembered, setRemembered] = useRememberedOpen(props.memory);
  const held = props.rows.find((row) => row.current);
  const current = held !== undefined;
  const open = remembered || current;
  const listId = useId();
  const whyId = useId();
  const list = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (remembered) list.current?.scrollIntoView?.({ block: "nearest" });
  }, [remembered]);
  return (
    <div className="lcr-rail-group lcr-rail-more">
      <span className="lcr-rail-item" data-held={held ? "" : undefined}>
        <button
          type="button"
          className="lcr-rail-row lcr-rail-more-toggle"
          aria-expanded={open}
          aria-controls={listId}
          // A folder on screen keeps the group open: closing it would hide where you are.
          aria-disabled={current || undefined}
          aria-describedby={held ? whyId : undefined}
          onClick={() => !current && setRemembered(!remembered)}
        >
          <NavGlyph icon="more" size={18} />
          <span className="lcr-rail-label">
            <span className="lcr-rail-label-text">More</span>
          </span>
          <span className="lcr-rail-more-caret" aria-hidden="true">
            <ChevronDownIcon size={16} />
          </span>
        </button>
        {/* Why the group cannot close now, to the eye on hover and focus, and to a screen reader. */}
        {held && (
          <>
            <span id={whyId} className="lcr-visually-hidden">
              Stays open while {held.label} is on screen
            </span>
            <Tip>Stays open while {held.label} is on screen</Tip>
          </>
        )}
      </span>
      <div id={listId} ref={list} className="lcr-rail-more-list" hidden={!open}>
        {props.rows.map((row) => (
          <Row key={row.key} row={row} onNavigate={props.onNavigate} />
        ))}
      </div>
    </div>
  );
}

/**
 * Whose threads the rows hold (step 3 of docs/2026-10-27-inbox-rail-scope-and-counts-plan.md): one
 * control at the head of the inbox's rows, whatever the team's size. Colleagues sit one level down,
 * by name, so twenty of them never push the label rows out of sight.
 */
function ScopeSwitch(props: { scope: ScopeNav; onNavigate: (href: string) => void }) {
  const { scope } = props;
  const choices = [...scope.fixed, ...scope.people];
  return (
    <span className="lcr-rail-item lcr-rail-owner">
      <OptionMenu<string>
        label="Showing"
        // No icon: the words carry it, and at the panel's 200 px the icon cost "Everyone" its end. It
        // starts on the client's name's line above, the two read as one head (critique, LEA-501).
        icon={null}
        value={scope.value}
        // Named as a scope, not a place: "Everyone" alone read as a page to open (critique, P2).
        display={
          <span className="lcr-rail-label">
            <span className="lcr-rail-owner-word">Showing</span> {scope.label}
          </span>
        }
        options={scopeOptions(scope)}
        onChange={(value) => {
          const href = choices.find((choice) => choice.value === value)?.href;
          if (href) props.onNavigate(href);
        }}
      />
      <Tip>Showing: {scope.label}</Tip>
    </span>
  );
}

/*
 * The inbox's rows, in their groups, as DESIGN.md orders them: Inbox and the work, the label views
 * under "Labels", the boxes, this person's saved views, then More. A hairline parts the groups.
 */
function PanelRows({
  nav,
  onNavigate,
  viewRow,
  moreMemory,
}: {
  nav: ShellNav;
  onNavigate: (href: string) => void;
  viewRow: InboxPanelProps["viewRow"] | undefined;
  moreMemory: string | undefined;
}) {
  const rows = (list: readonly NavRow[]) =>
    list.map((row) => <Row key={row.key} row={row} onNavigate={onNavigate} />);
  return (
    <>
      {mailGroups(nav.mail).map((group) => (
        <div
          key={group.key}
          className={group.label ? "lcr-rail-group lcr-rail-labels" : "lcr-rail-group"}
        >
          {group.label && <p className="lcr-label lcr-rail-group-label">{group.label}</p>}
          {rows(group.rows)}
        </div>
      ))}
      {nav.views.length > 0 && (
        <div className="lcr-rail-group lcr-rail-views">
          <p className="lcr-label lcr-rail-group-label">Views</p>
          {nav.views.map((row) => {
            const link = <Row key={row.key} row={row} onNavigate={onNavigate} />;
            return viewRow ? <Fragment key={row.key}>{viewRow(row, link)}</Fragment> : link;
          })}
        </div>
      )}
      {nav.more.length > 0 && (
        <MoreGroup rows={nav.more} memory={moreMemory} onNavigate={onNavigate} />
      )}
    </>
  );
}

/*
 * The client's own mark: its initials on a tile. A collapsed rail drew the same building glyph for
 * every client, so below 1,500 px nothing on screen said whose data this was without a hover
 * (after-critique of 2026-09-22). Two letters are enough to tell a rep's handful of clients apart.
 */
function Monogram({ name }: { name: string }) {
  return (
    <span className="lcr-rail-monogram" aria-hidden="true">
      {initials(name).toUpperCase()}
    </span>
  );
}

/** A workspace's name, folded for matching what was typed: case and accents do not count. */
const folded = (text: string): string =>
  text
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLowerCase();

/**
 * The client every view belongs to (LEA-501): its monogram at the rail's head, opening a switcher
 * that filters as you type, as a combobox does (WAI-ARIA APG): the field keeps the focus, the
 * arrows move the active choice, Enter opens it, Esc closes and returns to the monogram. Each client
 * shows the work waiting there, the one count the workspace list reads. "Add workspace" closes the
 * list for an agency admin. With one client and nothing to add, it is a label with a tip.
 */
function WorkspaceSwitcher(
  props: Pick<
    RailProps,
    | "current"
    | "workspaces"
    | "workspaceStatus"
    | "onWorkspace"
    | "onNavigate"
    | "addWorkspaceHref"
    | "switcherOpen"
    | "onSwitcherOpen"
  >,
) {
  const current = props.current;
  /*
   * The rail keeps its shape when there is no workspace to name, and says which state it is in.
   * Dropping the scope and the resources together is how this used to fail: the chrome went quiet
   * and the explanation appeared somewhere else entirely.
   */
  if (current === null)
    return (
      <span className="lcr-rail-item">
        <span className="lcr-workspace-status" role="img" aria-label={props.workspaceStatus}>
          <WorkspaceIcon size={18} />
        </span>
        <Tip>{props.workspaceStatus}</Tip>
      </span>
    );
  if (props.workspaces.length <= 1 && props.addWorkspaceHref === null)
    return (
      <span className="lcr-rail-item">
        <span className="lcr-workspace-name" role="img" aria-label={`Workspace: ${current.name}`}>
          <Monogram name={current.name} />
        </span>
        <Tip>{current.name}</Tip>
      </span>
    );
  return <SwitcherMenu {...props} current={current} />;
}

function SwitcherMenu(
  props: Parameters<typeof WorkspaceSwitcher>[0] & { readonly current: RailWorkspace },
) {
  const { current, switcherOpen: open, onSwitcherOpen: setOpen } = props;
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(0);
  const root = useRef<HTMLSpanElement>(null);
  const button = useRef<HTMLButtonElement>(null);
  const field = useRef<HTMLInputElement>(null);
  const listId = useId();
  const shown = props.workspaces.filter((workspace) =>
    folded(workspace.name).includes(folded(query.trim())),
  );
  const optionId = (index: number) => `${listId}-${index}`;
  useEffect(() => {
    if (!open) return;
    setQuery("");
    setActive(
      Math.max(
        0,
        props.workspaces.findIndex((item) => item.id === current.id),
      ),
    );
    field.current?.focus();
    const outside = (event: PointerEvent) => {
      if (event.target instanceof Node && root.current?.contains(event.target)) return;
      setOpen(false);
    };
    document.addEventListener("pointerdown", outside);
    return () => document.removeEventListener("pointerdown", outside);
  }, [open, setOpen, current.id, props.workspaces]);
  const close = (): void => {
    setOpen(false);
    button.current?.focus();
  };
  const choose = (id: string | undefined): void => {
    if (id === undefined) return;
    setOpen(false);
    if (id !== current.id) props.onWorkspace(id);
  };
  const onKey = (event: React.KeyboardEvent<HTMLInputElement>): void => {
    const step = event.key === "ArrowDown" ? 1 : event.key === "ArrowUp" ? -1 : 0;
    if (step !== 0 && shown.length > 0) {
      event.preventDefault();
      setActive((active + step + shown.length) % shown.length);
      return;
    }
    if (event.key === "Enter") {
      event.preventDefault();
      choose(shown[active]?.id);
      return;
    }
    if (event.key === "Escape") {
      event.preventDefault();
      close();
    }
  };
  return (
    <span className="lcr-rail-item lcr-switcher" ref={root}>
      <button
        ref={button}
        type="button"
        className="lcr-switcher-button"
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={`Workspace: ${current.name}. Switch workspace`}
        aria-keyshortcuts="W"
        onClick={() => setOpen(!open)}
      >
        <Monogram name={current.name} />
      </button>
      <Tip>{current.name}</Tip>
      {open && (
        <div
          className="lcr-popover lcr-switcher-popover"
          role="dialog"
          aria-label="Switch workspace"
        >
          <input
            ref={field}
            className="lcr-switcher-field"
            type="text"
            role="combobox"
            aria-expanded="true"
            aria-controls={listId}
            aria-activedescendant={shown[active] ? optionId(active) : undefined}
            aria-autocomplete="list"
            aria-label="Find a workspace"
            placeholder="Find a workspace"
            value={query}
            onChange={(event) => {
              setQuery(event.target.value);
              setActive(0);
            }}
            onKeyDown={onKey}
          />
          {/* A press in the list must not take focus out of the field that drives it. */}
          <div
            id={listId}
            className="lcr-switcher-list"
            role="listbox"
            aria-label="Workspaces"
            onMouseDown={(event) => event.preventDefault()}
          >
            {shown.map((workspace, index) => (
              // biome-ignore lint/a11y/useKeyWithClickEvents: the keys are handled on the field, which keeps focus (aria-activedescendant)
              <div
                key={workspace.id}
                id={optionId(index)}
                role="option"
                tabIndex={-1}
                aria-selected={index === active}
                aria-current={workspace.id === current.id || undefined}
                className="lcr-switcher-option"
                onPointerEnter={() => setActive(index)}
                onClick={() => choose(workspace.id)}
              >
                <Monogram name={workspace.name} />
                <span className="lcr-switcher-name">{workspace.name}</span>
                {/*
                 * Yours to answer, by the switcher's own rule (Dawid, 2026-10-20: mine or
                 * unassigned), so it says so: beside the rail's Inbox, which counts everyone's work,
                 * a bare 47 against 49 read as a contradiction (critique, LEA-501).
                 */}
                {workspace.needsReply ? (
                  <span className="lcr-switcher-count">
                    <span aria-hidden="true">{counted(workspace.needsReply)} for you</span>
                    <span className="lcr-visually-hidden">
                      , {counted(workspace.needsReply)} need a reply, yours or unassigned
                    </span>
                  </span>
                ) : null}
                {workspace.id === current.id && <CheckIcon size={16} />}
              </div>
            ))}
          </div>
          {shown.length === 0 && (
            <p className="lcr-switcher-empty" role="status">
              No workspace matches “{query.trim()}”.
            </p>
          )}
          {props.addWorkspaceHref && (
            <a
              className="lcr-switcher-add"
              href={props.addWorkspaceHref}
              onClick={(event) => {
                if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
                event.preventDefault();
                setOpen(false);
                if (props.addWorkspaceHref) props.onNavigate(props.addWorkspaceHref);
              }}
            >
              <PlusIcon size={16} />
              Add workspace
            </a>
          )}
        </div>
      )}
    </span>
  );
}

const menuItems = (root: HTMLElement | null): HTMLElement[] => [
  ...(root?.querySelectorAll<HTMLElement>('[role^="menuitem"]') ?? []),
];

/*
 * Who you are and what this browser shows, behind one control. The foot used to stack an avatar that
 * did nothing, a shortcuts button, a theme button and sign out, one above the other in 64 px, on a
 * different axis from the rows above them (Dawid, 2026-09-22: "the stack of 4 icons at the bottom
 * needs redesign"). The phone's tray already groups the same things the same way.
 */
function AccountMenu(
  props: Pick<
    RailProps,
    | "user"
    | "dark"
    | "onToggleTheme"
    | "onSignOut"
    | "onShortcuts"
    | "displayOptions"
    | "onNavigate"
  >,
) {
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLSpanElement>(null);
  const button = useRef<HTMLButtonElement>(null);
  const menuId = useId();
  useEffect(() => {
    if (!open) return;
    menuItems(root.current)[0]?.focus();
    const outside = (event: PointerEvent) => {
      if (event.target instanceof Node && root.current?.contains(event.target)) return;
      setOpen(false);
    };
    document.addEventListener("pointerdown", outside);
    return () => document.removeEventListener("pointerdown", outside);
  }, [open]);

  const close = (): void => {
    setOpen(false);
    button.current?.focus();
  };
  const move = (event: React.KeyboardEvent<HTMLDivElement>): void => {
    if (event.key === "Escape" || event.key === "Tab") {
      if (event.key === "Escape") event.preventDefault();
      close();
      return;
    }
    const all = menuItems(root.current);
    const index =
      document.activeElement instanceof HTMLElement ? all.indexOf(document.activeElement) : -1;
    const target =
      event.key === "ArrowDown"
        ? all[(index + 1) % all.length]
        : event.key === "ArrowUp"
          ? all[(index - 1 + all.length) % all.length]
          : event.key === "Home"
            ? all[0]
            : event.key === "End"
              ? all.at(-1)
              : undefined;
    if (!target) return;
    event.preventDefault();
    target.focus();
  };

  return (
    <span className="lcr-rail-item lcr-rail-account" ref={root}>
      <button
        ref={button}
        type="button"
        className="lcr-rail-account-button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        aria-label={`Account, ${props.user.name}`}
        onClick={() => setOpen(!open)}
        onKeyDown={(event) => {
          if (event.key !== "ArrowUp" || open) return;
          event.preventDefault();
          setOpen(true);
        }}
      >
        <span className="lcr-rail-avatar" aria-hidden="true">
          {initials(props.user.name)}
        </span>
        <span className="lcr-rail-label lcr-rail-account-who">
          <span className="lcr-rail-account-name">{props.user.name}</span>
          <span className="lcr-rail-account-email">{props.user.email}</span>
        </span>
      </button>
      <Tip>{props.user.name}</Tip>
      {open && (
        <div
          className="lcr-popover lcr-rail-account-menu"
          id={menuId}
          role="menu"
          aria-label="Account"
          onKeyDown={move}
        >
          <div className="lcr-rail-account-head">
            <span className="lcr-rail-account-name">{props.user.name}</span>
            <span className="lcr-rail-account-email">{props.user.email}</span>
          </div>
          <a
            role="menuitem"
            className="lcr-menu-item lcr-rail-account-item"
            href="/account/connections"
            onClick={(event) => {
              if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
              event.preventDefault();
              setOpen(false);
              props.onNavigate("/account/connections");
            }}
          >
            <LinkIcon size={18} />
            <span className="lcr-menu-item-text">Connected apps</span>
          </a>
          {props.onShortcuts && (
            <button
              type="button"
              role="menuitem"
              className="lcr-menu-item lcr-rail-account-item lcr-rail-account-shortcuts"
              onClick={() => {
                setOpen(false);
                // The panel gives the focus back to where it came from: the menu's own button.
                button.current?.focus();
                props.onShortcuts?.();
              }}
            >
              <ShortcutsIcon size={18} />
              <span className="lcr-menu-item-text">Keyboard shortcuts</span>
              <kbd className="lcr-kbd">?</kbd>
            </button>
          )}
          <button
            type="button"
            role="menuitemcheckbox"
            aria-checked={props.dark}
            className="lcr-menu-item lcr-rail-account-item"
            onClick={props.onToggleTheme}
          >
            <MoonIcon size={18} />
            <span className="lcr-menu-item-text">Dark mode</span>
            {props.dark && <CheckIcon size={16} />}
          </button>
          {props.displayOptions}
          <hr className="lcr-rail-account-rule" />
          <button
            type="button"
            role="menuitem"
            className="lcr-menu-item lcr-rail-account-item"
            onClick={() => {
              setOpen(false);
              props.onSignOut();
            }}
          >
            <LogOutIcon size={18} />
            <span className="lcr-menu-item-text">Sign out</span>
          </button>
        </div>
      )}
    </span>
  );
}

/**
 * The app rail: the brand, the client, the sections, then Settings and the account at the foot.
 * The section on screen is in ink; it carries the gold stroke only where it has no panel row to
 * carry it (DESIGN.md: the deeper level wins), which in the inbox is when the panel has folded.
 */
export function Rail(props: RailProps) {
  const home = props.nav?.home ?? "/";
  const sections = props.nav?.sections ?? [];
  return (
    <nav id="lcr-rail" className="lcr-rail" data-state="collapsed" aria-label="Sections">
      <div className="lcr-rail-head">
        <a
          className="lcr-rail-brand"
          href={home}
          aria-label="LeadCue inbox"
          onClick={(event) => {
            if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
            event.preventDefault();
            props.onNavigate(home);
          }}
        >
          {/* At 64 px the app mark carries the brand; the wordmark has no room here. */}
          <img className="lcr-rail-mark" src="/leadcue-mark.svg" alt="" />
        </a>
      </div>

      <div className="lcr-rail-scope">
        <WorkspaceSwitcher
          current={props.current}
          workspaces={props.workspaces}
          workspaceStatus={props.workspaceStatus}
          onWorkspace={props.onWorkspace}
          onNavigate={props.onNavigate}
          addWorkspaceHref={props.addWorkspaceHref}
          switcherOpen={props.switcherOpen}
          onSwitcherOpen={props.onSwitcherOpen}
        />
      </div>

      <div className="lcr-rail-nav">
        {sections.map((row) => (
          <Row
            key={row.key}
            row={
              row.key === INBOX_SECTION && props.panelShown && row.current
                ? { ...row, marked: false }
                : row
            }
            onNavigate={props.onNavigate}
            noun={row.key === INBOX_SECTION ? "need a reply" : "conversations"}
            icons
          />
        ))}
      </div>

      <div className="lcr-rail-util">
        {props.nav?.settings && (
          <Row row={props.nav.settings} onNavigate={props.onNavigate} icons />
        )}
        <AccountMenu
          user={props.user}
          dark={props.dark}
          onToggleTheme={props.onToggleTheme}
          onSignOut={props.onSignOut}
          onNavigate={props.onNavigate}
          {...(props.onShortcuts ? { onShortcuts: props.onShortcuts } : {})}
          displayOptions={props.displayOptions}
        />
        {props.footPanel}
      </div>
    </nav>
  );
}

/**
 * The inbox's panel: the client named at its head with whose threads the rows hold (the Showing
 * control, once anything is assigned), then the rows. It scrolls on its own when a short window
 * cannot hold them, and the current row scrolls into view.
 */
export function InboxPanel(props: InboxPanelProps) {
  const { nav } = props;
  return (
    <nav
      className="lcr-rail lcr-panel"
      data-state="expanded"
      style={{ width: `${props.width}px` }}
      aria-label="Inbox"
    >
      <div className="lcr-panel-head">
        <p className="lcr-panel-title" title={props.workspaceName}>
          {props.workspaceName}
        </p>
        {nav.scope && <ScopeSwitch scope={nav.scope} onNavigate={props.onNavigate} />}
      </div>
      <div className="lcr-rail-nav">
        <PanelRows
          nav={nav}
          onNavigate={props.onNavigate}
          viewRow={props.viewRow}
          moreMemory={props.moreMemory}
        />
      </div>
    </nav>
  );
}
